import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'

const PAGE_SIZE = 1000

export interface StreamingReportRow {
  service: string
  content_type: string
  access_model: string
  catalogue_expires_at: string
  audio_description_available: boolean | null
  subtitles_available: boolean | null
  sign_language_available: boolean | null
}

export interface StreamingReportStats {
  total: number
  withAd: number
  withSubtitles: number
  withBsl: number
  withAnyAccessibility: number
  withNone: number
}

function emptyStats(): StreamingReportStats {
  return {
    total: 0,
    withAd: 0,
    withSubtitles: 0,
    withBsl: 0,
    withAnyAccessibility: 0,
    withNone: 0,
  }
}

function addRow(s: StreamingReportStats, row: StreamingReportRow) {
  s.total += 1
  if (row.audio_description_available === true) s.withAd += 1
  if (row.subtitles_available === true) s.withSubtitles += 1
  if (row.sign_language_available === true) s.withBsl += 1
  const any =
    row.audio_description_available === true ||
    row.subtitles_available === true ||
    row.sign_language_available === true
  if (any) s.withAnyAccessibility += 1
  else s.withNone += 1
}

export interface StreamingReportData {
  windowLabel: string
  overall: StreamingReportStats
  /** Counts by calendar day (UTC) of catalogue_expires_at */
  byExpiryDay: { dayKey: string; stats: StreamingReportStats }[]
  byService: { service: string; stats: StreamingReportStats }[]
  byContentType: { contentType: string; stats: StreamingReportStats }[]
  byAccessModel: { accessModel: string; stats: StreamingReportStats }[]
  rowCount: number
  includeExpired: boolean
}

export const ADMIN_STREAMING_REPORT_QUERY_KEY = ['admin', 'streaming-report'] as const

export interface UseAdminStreamingReportParams {
  includeExpired: boolean
  /** Inclusive UTC date yyyy-MM-dd: only rows with catalogue_expires_at on or after this instant */
  expiresFrom: string | null
  /** Inclusive UTC date yyyy-MM-dd: only rows with catalogue_expires_at before end of this day */
  expiresTo: string | null
}

export function useAdminStreamingReport(enabled: boolean, params: UseAdminStreamingReportParams) {
  const { includeExpired, expiresFrom, expiresTo } = params
  return useQuery({
    queryKey: [...ADMIN_STREAMING_REPORT_QUERY_KEY, includeExpired, expiresFrom, expiresTo],
    enabled,
    staleTime: 60_000,
    queryFn: async (): Promise<StreamingReportData> => {
      const rows: StreamingReportRow[] = []
      const nowIso = new Date().toISOString()

      for (let from = 0; ; from += PAGE_SIZE) {
        let q = supabase
          .from('streaming_listings')
          .select(
            'service, content_type, access_model, catalogue_expires_at, audio_description_available, subtitles_available, sign_language_available'
          )
          .order('catalogue_expires_at', { ascending: true })
          .range(from, from + PAGE_SIZE - 1)

        if (!includeExpired) {
          // Legacy rows may have NULL expiry; include them so report is not blank before backfill.
          q = q.or(`catalogue_expires_at.is.null,catalogue_expires_at.gt.${nowIso}`)
        }
        if (expiresFrom) {
          q = q.gte('catalogue_expires_at', `${expiresFrom}T00:00:00.000Z`)
        }
        if (expiresTo) {
          const end = new Date(`${expiresTo}T00:00:00.000Z`)
          end.setUTCDate(end.getUTCDate() + 1)
          q = q.lt('catalogue_expires_at', end.toISOString())
        }

        const { data, error } = await q
        if (error) throw error
        const batch = (data ?? []) as StreamingReportRow[]
        if (batch.length === 0) break
        rows.push(...batch)
        if (batch.length < PAGE_SIZE) break
      }

      const overall = emptyStats()
      const byExpiryDayMap = new Map<string, StreamingReportStats>()
      const byServiceMap = new Map<string, StreamingReportStats>()
      const byContentTypeMap = new Map<string, StreamingReportStats>()
      const byAccessModelMap = new Map<string, StreamingReportStats>()

      for (const row of rows) {
        addRow(overall, row)

        const dayKey = row.catalogue_expires_at.slice(0, 10)
        let d = byExpiryDayMap.get(dayKey)
        if (!d) {
          d = emptyStats()
          byExpiryDayMap.set(dayKey, d)
        }
        addRow(d, row)

        const svc = row.service || '(unknown)'
        let s = byServiceMap.get(svc)
        if (!s) {
          s = emptyStats()
          byServiceMap.set(svc, s)
        }
        addRow(s, row)

        const ct = row.content_type || '(unknown)'
        let c = byContentTypeMap.get(ct)
        if (!c) {
          c = emptyStats()
          byContentTypeMap.set(ct, c)
        }
        addRow(c, row)

        const am = row.access_model || '(unknown)'
        let a = byAccessModelMap.get(am)
        if (!a) {
          a = emptyStats()
          byAccessModelMap.set(am, a)
        }
        addRow(a, row)
      }

      const byExpiryDay = Array.from(byExpiryDayMap.entries())
        .sort(([x], [y]) => x.localeCompare(y))
        .map(([dayKey, stats]) => ({ dayKey, stats }))

      const byService = Array.from(byServiceMap.entries())
        .map(([service, stats]) => ({ service, stats }))
        .sort((a, b) => b.stats.total - a.stats.total)

      const byContentType = Array.from(byContentTypeMap.entries())
        .map(([contentType, stats]) => ({ contentType, stats }))
        .sort((a, b) => b.stats.total - a.stats.total)

      const byAccessModel = Array.from(byAccessModelMap.entries())
        .map(([accessModel, stats]) => ({ accessModel, stats }))
        .sort((a, b) => b.stats.total - a.stats.total)

      const windowLabel = [
        includeExpired ? 'Including expired rows' : 'Non-expired only',
        expiresFrom && expiresTo
          ? `Expires (UTC): ${expiresFrom} → ${expiresTo} (inclusive)`
          : expiresFrom
            ? `Expires on or after ${expiresFrom} (UTC)`
            : expiresTo
              ? `Expires on or before ${expiresTo} (UTC)`
              : 'No expiry date filter',
      ].join(' · ')

      return {
        windowLabel,
        overall,
        byExpiryDay,
        byService,
        byContentType,
        byAccessModel,
        rowCount: rows.length,
        includeExpired,
      }
    },
  })
}
